/**
 * ConditionBuilder — drzewo warunków reguły uprawnień warunkowych (F24).
 *
 * Buduje zdanie w rodzaju „kwota brutto poniżej 5000 **i** (kontrahent aktywny **lub** opiekun to
 * ja)". Grupy da się zagnieżdżać do granicy przyjmowanej przez zapis; wybór pola i operatorów
 * pochodzi z serwera, więc ekran nie zaproponuje niczego, czego zapis nie przyjmie.
 *
 * **Dlaczego to NIE jest ten sam komponent, co w kreatorze procesów**, mimo podobnego wyglądu:
 * tam niewypełniony warunek znaczy „nie blokuj", tutaj — „nie przyznawaj". Wspólny komponent
 * musiałby nosić tę różnicę w sobie i prędzej czy później zostałaby przeoczona. Wygląd trzymamy
 * spójny świadomie; zachowanie jest z założenia inne.
 */

import { useTranslation } from 'react-i18next';
import {
  CONDITION_LIMITS,
  CONDITION_PLACEHOLDERS,
  conditionDepth,
  isCurrentUserPlaceholder,
  LIST_OPERATORS,
  type PermissionCondition,
  type PermissionConditionLeaf,
  type PermissionConditionOperator,
  validateConditionShape,
  VALUELESS_OPERATORS,
} from '@saas/shared';
import { Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/Button';
import { IconButton } from '@/components/IconButton';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import type { ConditionFieldGroup, ConditionFieldOption } from '@/lib/use-condition-rules';

import { ConditionFieldPicker } from './ConditionFieldPicker';

/** Operatory sensowne dla danego rodzaju pola — reszta tylko myliłaby przy wyborze. */
const OPERATORS_BY_KIND: Record<ConditionFieldOption['kind'], PermissionConditionOperator[]> = {
  text: [
    'eq',
    'neq',
    'contains',
    'not_contains',
    'starts_with',
    'in',
    'not_in',
    'is_null',
    'is_not_null',
  ],
  number: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'in', 'not_in', 'is_null', 'is_not_null'],
  date: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'is_null', 'is_not_null'],
  timestamp: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'is_null', 'is_not_null'],
  boolean: ['eq', 'is_null', 'is_not_null'],
  uuid: ['eq', 'neq', 'in', 'not_in', 'is_null', 'is_not_null'],
  text_array: ['contains', 'not_contains', 'in', 'not_in', 'is_null', 'is_not_null'],
};

export interface ConditionBuilderProps {
  value: PermissionCondition;
  onChange: (next: PermissionCondition) => void;
  groups: ConditionFieldGroup[];
  disabled?: boolean;
}

export function ConditionBuilder({ value, onChange, groups, disabled }: ConditionBuilderProps) {
  return (
    <ConditionNode
      node={value}
      onChange={onChange}
      onRemove={null}
      groups={groups}
      depth={1}
      disabled={disabled}
    />
  );
}

interface NodeProps {
  node: PermissionCondition;
  onChange: (next: PermissionCondition) => void;
  onRemove: (() => void) | null;
  groups: ConditionFieldGroup[];
  depth: number;
  disabled?: boolean;
}

function ConditionNode({ node, onChange, onRemove, groups, depth, disabled }: NodeProps) {
  const { t } = useTranslation();

  if (node.kind === 'leaf') {
    return (
      <LeafRow
        leaf={node}
        onChange={onChange}
        onRemove={onRemove}
        groups={groups}
        disabled={disabled}
      />
    );
  }

  const canNest = depth < CONDITION_LIMITS.maxDepth;

  const replaceChild = (index: number, next: PermissionCondition) => {
    const children = [...node.children];
    children[index] = next;
    onChange({ ...node, children });
  };

  const removeChild = (index: number) => {
    const children = node.children.filter((_, i) => i !== index);
    // Grupa bez warunków nie ma prawa powstać — pusty warunek nie przyznaje niczego, a zapis
    // i tak by go odrzucił. Zamiast zostawiać ekran w stanie nie do zapisania, znika cała grupa.
    if (children.length === 0 && onRemove) {
      onRemove();
      return;
    }
    onChange({ ...node, children });
  };

  return (
    <div className="rounded-md border border-border-primary p-2">
      <div className="mb-2 flex items-center gap-2">
        <Select
          aria-label={t('tenant.permissions.conditions.logic')}
          value={node.logic}
          disabled={disabled}
          onChange={(e) => onChange({ ...node, logic: e.target.value === 'OR' ? 'OR' : 'AND' })}
          options={[
            { value: 'AND', label: t('tenant.permissions.conditions.and') },
            { value: 'OR', label: t('tenant.permissions.conditions.or') },
          ]}
          containerClassName="w-40"
        />
        <span className="text-xs text-text-secondary">
          {node.logic === 'AND'
            ? t('tenant.permissions.conditions.andHint')
            : t('tenant.permissions.conditions.orHint')}
        </span>
        <div className="ml-auto flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={() => onChange({ ...node, children: [...node.children, emptyLeaf(groups)] })}
          >
            <Plus className="mr-1 h-3 w-3" />
            {t('tenant.permissions.conditions.addCondition')}
          </Button>
          {canNest ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={disabled}
              onClick={() =>
                onChange({
                  ...node,
                  children: [
                    ...node.children,
                    { kind: 'group', logic: 'OR', children: [emptyLeaf(groups)] },
                  ],
                })
              }
            >
              <Plus className="mr-1 h-3 w-3" />
              {t('tenant.permissions.conditions.addGroup')}
            </Button>
          ) : null}
          {onRemove ? (
            <IconButton
              size="sm"
              variant="ghost"
              aria-label={t('tenant.permissions.conditions.removeGroup')}
              disabled={disabled}
              onClick={onRemove}
            >
              <Trash2 className="h-3 w-3" />
            </IconButton>
          ) : null}
        </div>
      </div>

      <div className="flex flex-col gap-2 pl-3">
        {node.children.map((child, index) => (
          <ConditionNode
            key={index}
            node={child}
            onChange={(next) => replaceChild(index, next)}
            onRemove={() => removeChild(index)}
            groups={groups}
            depth={depth + 1}
            disabled={disabled}
          />
        ))}
      </div>
    </div>
  );
}

interface LeafProps {
  leaf: PermissionConditionLeaf;
  onChange: (next: PermissionCondition) => void;
  onRemove: (() => void) | null;
  groups: ConditionFieldGroup[];
  disabled?: boolean;
}

function LeafRow({ leaf, onChange, onRemove, groups, disabled }: LeafProps) {
  const { t } = useTranslation();
  const field = findField(groups, leaf.field);
  const operators: PermissionConditionOperator[] = field ? OPERATORS_BY_KIND[field.kind] : ['eq'];
  const takesValue = !VALUELESS_OPERATORS.has(leaf.operator);
  const takesList = LIST_OPERATORS.has(leaf.operator);

  return (
    <div className="flex flex-wrap items-end gap-2">
      <ConditionFieldPicker
        value={leaf.field}
        groups={groups}
        disabled={disabled}
        onChange={(path) => {
          // Ponowny klik zaznaczonego pola zgłasza to samo pole (`ConditionFieldPicker`) — to nie
          // zmiana, więc wybrana wartość zostaje.
          if (path === leaf.field) return;
          const next = findField(groups, path);
          // Zmiana pola potrafi unieważnić operator (np. „zaczyna się od" na liczbie) — wtedy
          // wracamy do porównania równości zamiast zostawiać stan nie do zapisania.
          const allowed: PermissionConditionOperator[] = next
            ? OPERATORS_BY_KIND[next.kind]
            : ['eq'];
          const operator = allowed.includes(leaf.operator) ? leaf.operator : allowed[0]!;
          onChange({ ...leaf, field: path, operator, value: shownDefaultValue(next, operator) });
        }}
      />

      <Select
        aria-label={t('tenant.permissions.conditions.operator')}
        value={leaf.operator}
        disabled={disabled}
        onChange={(e) => {
          const operator = e.target.value as PermissionConditionOperator;
          // Powrót z „wypełnione" / „nie wypełnione" do operatora z wartością: warunek nie ma jeszcze
          // wartości, więc dostaje tę, którą lista „Wartość" pokaże (patrz `shownDefaultValue`).
          const hasValue = leaf.value !== null && leaf.value !== '';
          onChange({
            ...leaf,
            operator,
            value: VALUELESS_OPERATORS.has(operator)
              ? null
              : hasValue
                ? leaf.value
                : shownDefaultValue(field, operator),
          });
        }}
        options={operators.map((op) => ({
          value: op,
          label: t(`tenant.permissions.conditions.op.${op}`),
        }))}
        containerClassName="min-w-44"
      />

      {takesValue ? (
        <ValueInput
          leaf={leaf}
          field={field}
          takesList={takesList}
          disabled={disabled}
          onChange={onChange}
        />
      ) : null}

      {onRemove ? (
        <IconButton
          size="sm"
          variant="ghost"
          aria-label={t('tenant.permissions.conditions.removeCondition')}
          disabled={disabled}
          onClick={onRemove}
        >
          <Trash2 className="h-3 w-3" />
        </IconButton>
      ) : null}
    </div>
  );
}

interface ValueProps {
  leaf: PermissionConditionLeaf;
  field: ConditionFieldOption | null;
  takesList: boolean;
  disabled?: boolean;
  onChange: (next: PermissionCondition) => void;
}

function ValueInput({ leaf, field, takesList, disabled, onChange }: ValueProps) {
  const { t } = useTranslation();

  // Pole trzymające identyfikator osoby albo jednostki przyjmuje podstawienie `@userId`
  // (zalogowany użytkownik) —
  // bez niego reguła byłaby taka sama dla wszystkich i cała rzecz traciłaby sens.
  if (field?.subject) {
    const options = [
      { value: CONDITION_PLACEHOLDERS.me, label: t('tenant.permissions.conditions.me') },
      ...(field.subject === 'user'
        ? [
            {
              value: CONDITION_PLACEHOLDERS.mySubordinates,
              label: t('tenant.permissions.conditions.mySubordinates'),
            },
          ]
        : []),
      ...(field.subject === 'org_unit'
        ? [
            {
              value: CONDITION_PLACEHOLDERS.myOrgUnit,
              label: t('tenant.permissions.conditions.myOrgUnit'),
            },
          ]
        : []),
    ];
    // Reguła zapisana dawnym `@me` pokazuje się jako `@userId` — to ten sam znacznik.
    const selected =
      typeof leaf.value === 'string' && !isCurrentUserPlaceholder(leaf.value)
        ? leaf.value
        : CONDITION_PLACEHOLDERS.me;
    return (
      <Select
        aria-label={t('tenant.permissions.conditions.value')}
        value={selected}
        disabled={disabled}
        onChange={(e) => onChange({ ...leaf, value: e.target.value })}
        options={options}
        containerClassName="min-w-56"
      />
    );
  }

  if (field?.kind === 'boolean') {
    return (
      <Select
        aria-label={t('tenant.permissions.conditions.value')}
        value={String(leaf.value ?? 'true')}
        disabled={disabled}
        onChange={(e) => onChange({ ...leaf, value: e.target.value === 'true' })}
        options={[
          { value: 'true', label: t('common.yes') },
          { value: 'false', label: t('common.no') },
        ]}
        containerClassName="min-w-32"
      />
    );
  }

  const asText = Array.isArray(leaf.value) ? leaf.value.join(', ') : (leaf.value ?? '');

  return (
    <Input
      aria-label={t('tenant.permissions.conditions.value')}
      type={field?.kind === 'date' ? 'date' : field?.kind === 'number' ? 'number' : 'text'}
      value={String(asText)}
      disabled={disabled}
      placeholder={takesList ? t('tenant.permissions.conditions.listHint') : undefined}
      onChange={(e) => {
        const raw = e.target.value;
        onChange({
          ...leaf,
          // Lista wartości wpisywana po przecinku — najkrótsza droga dla kilku statusów naraz.
          // Skasowany wpis pojedynczej wartości = brak wartości (nie pusty tekst ani 0): ekran pokazuje
          // puste pole, a zapis odrzuci niewypełniony warunek, zamiast zapisać porównanie z pustym.
          value: takesList
            ? raw
                .split(',')
                .map((s) => s.trim())
                .filter((s) => s.length > 0)
            : raw.trim() === ''
              ? null
              : field?.kind === 'number'
                ? Number(raw)
                : raw,
        });
      }}
      containerClassName="min-w-56"
    />
  );
}

function findField(groups: ConditionFieldGroup[], path: string): ConditionFieldOption | null {
  for (const group of groups) {
    const found = group.fields.find((f) => f.path === path);
    if (found) return found;
  }
  return null;
}

/**
 * Wartość, którą lista „Wartość" pokazuje, zanim użytkownik coś w niej wybierze: przy polu z osobą
 * albo jednostką — `@userId` (zalogowany użytkownik), przy polu Tak / Nie — „Tak". Warunek musi ją nieść od razu: inaczej
 * ekran pokazuje wybór, którego w regule nie ma, a zapis kończy się odmową serwera (brak wartości).
 * Operator bez wartości („wypełnione" / „nie wypełnione") i pozostałe rodzaje pól → `null` (pole
 * tekstowe i liczbowe startuje puste — wartość trzeba wpisać).
 */
function shownDefaultValue(
  field: ConditionFieldOption | null,
  operator: PermissionConditionOperator,
): PermissionConditionLeaf['value'] {
  if (VALUELESS_OPERATORS.has(operator)) return null;
  if (field?.subject) return CONDITION_PLACEHOLDERS.me;
  if (field?.kind === 'boolean') return true;
  return null;
}

/**
 * Nowy warunek startuje na pierwszym dostępnym polu — pusty wybór nie dałby się zapisać. Wartość —
 * ta, którą pokaże lista „Wartość"; pole tekstowe i liczbowe startuje bez wartości (nie pustym
 * napisem), tak jak po skasowaniu wpisu — niewypełniony warunek zapis odrzuca, zamiast zapisać
 * porównanie z pustym tekstem (tu niewypełniony warunek nie przyznaje niczego — nagłówek pliku).
 */
function emptyLeaf(groups: ConditionFieldGroup[]): PermissionCondition {
  const first = groups[0]?.fields[0] ?? null;
  return {
    kind: 'leaf',
    field: first?.path ?? '',
    operator: 'eq',
    value: shownDefaultValue(first, 'eq'),
  };
}

/** Warunek startowy nowej reguły. */
export function emptyCondition(groups: ConditionFieldGroup[]): PermissionCondition {
  return { kind: 'group', logic: 'AND', children: [emptyLeaf(groups)] };
}

/** Czy warunek nie przekracza dozwolonego zagnieżdżenia — panel blokuje zapis wcześniej niż serwer. */
export function conditionTooDeep(condition: PermissionCondition): boolean {
  return conditionDepth(condition) > CONDITION_LIMITS.maxDepth;
}

/**
 * Czy któryś warunek nie ma wartości (albo listy przy „jedna z" / „żadna z") — ten sam walidator
 * kształtu co zapis na serwerze. Panel blokuje wtedy zapis i mówi, co uzupełnić, zamiast wysłać
 * regułę, którą serwer odrzuci ogólnym błędem.
 */
export function conditionMissesValue(condition: PermissionCondition): boolean {
  return validateConditionShape(condition).some(
    (error) => error === 'VALUE_REQUIRED' || error === 'LIST_REQUIRED',
  );
}
